import { __ } from '@wordpress/i18n';
import { createColumnHelper } from '@tanstack/react-table';
import { ShareButton } from '../ShareButton';

const col = createColumnHelper();

/**
 * Helper: flatten a WPGraphQL connection to a comma-separated string.
 * Taxonomy pick fields come back as { nodes: [{ name }] }.
 * @param connection
 */
const taxNames = ( connection ) =>
	connection?.nodes?.map( ( n ) => n.name ).join( ', ' ) ?? '';

/**
 * filterFn for taxonomy columns: checks whether the selected term name appears
 * in the raw WPGraphQL connection stored on row.original[columnId].
 * @param row
 * @param columnId
 * @param filterValue
 */
function taxFilterFn( row, columnId, filterValue ) {
	const connection = row.original[ columnId ];
	return connection?.nodes?.some( ( n ) => n.name === filterValue ) ?? false;
}
taxFilterFn.autoRemove = ( val ) => ! val;

const isOwnOrg = ( row ) =>
	row.original.author?.node?.databaseId ===
	Number( window.anacrm?.currentUserId );

/**
 * Column definitions for the org CPT.
 *
 * Field names follow WPGraphQL for Pods convention: camelCase, no leading underscore.
 * Verify against /graphql schema explorer if any field returns undefined.
 * @param root0
 * @param root0.onDelete
 * @param root0.onTogglePrivate
 * @param root0.onReclaim
 * @param root0.onSuggest
 */
export function orgColumns( { onDelete, onTogglePrivate, onReclaim, onSuggest } ) {
	return [
		col.display( {
			id: 'actions',
			header: '',
			cell: ( { row } ) => (
				<div className="flex flex-col gap-1 items-start">
					{ window.anacrm?.isStaff && (
						<button
							onClick={ () => {
								if (
									! window.confirm(
										__(
											'Move this organisation to trash?',
											'anacrm'
										)
									)
								) {
									return;
								}
								onDelete( row.original.databaseId );
							} }
							className="px-2 py-1 text-xs border-2 border-red bg-white hover:bg-red hover:text-white cursor-pointer font-semibold whitespace-nowrap"
						>
							{ __( 'Delete', 'anacrm' ) }
						</button>
					) }
					{ window.anacrm?.isStaff && (
						<button
							onClick={ () =>
								onTogglePrivate(
									row.original.databaseId,
									! row.original.private
								)
							}
							className="px-2 py-1 text-xs border-2 border-green-dark bg-white hover:bg-yellow cursor-pointer font-semibold whitespace-nowrap"
						>
							{ row.original.private
								? __( 'Make public', 'anacrm' )
								: __( 'Privatize', 'anacrm' ) }
						</button>
					) }
					{ window.anacrm?.canReclaimOrgs &&
						isOwnOrg( row ) && (
							<span className="text-xs font-semibold whitespace-nowrap">
								{ __( 'This is your org', 'anacrm' ) }
							</span>
						) }
					{ window.anacrm?.canReclaimOrgs &&
						! isOwnOrg( row ) && (
							<button
								onClick={ () =>
									onReclaim( row.original.databaseId )
								}
								className="px-2 py-1 text-xs border-2 border-green-dark bg-white hover:bg-yellow cursor-pointer font-semibold whitespace-nowrap"
							>
								{ __( 'Reclaim this org', 'anacrm' ) }
							</button>
						) }
					{ window.anacrm?.canSuggestOrgs &&
						! isOwnOrg( row ) && (
							<button
								onClick={ () =>
									onSuggest( row.original.databaseId )
								}
								className="px-2 py-1 text-xs border-2 border-green-dark bg-white hover:bg-yellow cursor-pointer font-semibold whitespace-nowrap"
							>
								{ __( 'Suggest changes', 'anacrm' ) }
							</button>
						) }
					<ShareButton uri={ row.original.uri } />
				</div>
			),
		} ),

		col.accessor( 'title', {
			header: __( 'Name', 'anacrm' ),
			cell: ( { row, getValue } ) => (
				<div className="flex items-center gap-2">
					<a
						href={ row.original.uri }
						className="font-semibold text-blue hover:!no-underline"
					>
						{ getValue() }
					</a>
					{ row.original.private && (
						<span className="inline-block align-middle text-xs font-semibold px-2 py-0.5 bg-red text-white whitespace-nowrap">
							{ __( 'Private', 'anacrm' ) }
						</span>
					) }
				</div>
			),
		} ),

		col.accessor( ( row ) => taxNames( row.territories ), {
			id: 'territories',
			header: __( 'Territory', 'anacrm' ),
			filterFn: taxFilterFn,
		} ),

		col.accessor( ( row ) => taxNames( row.regions ), {
			id: 'regions',
			header: __( 'Region', 'anacrm' ),
			filterFn: taxFilterFn,
		} ),

		col.accessor( ( row ) => taxNames( row.cities ), {
			id: 'cities',
			header: __( 'Municipality', 'anacrm' ),
			filterFn: taxFilterFn,
		} ),

		col.accessor( 'contactMail', {
			header: __( 'Email', 'anacrm' ),
			cell: ( { getValue } ) => {
				const v = getValue();
				if ( ! v ) {
					return '—';
				}
				const emails = v
					.split( ',' )
					.map( ( e ) => e.trim() )
					.filter( Boolean );
				return emails.map( ( email, i ) => (
					<a
						key={ i }
						href={ `mailto:${ email }` }
						className="text-blue hover:!no-underline block"
					>
						{ email }
					</a>
				) );
			},
		} ),

		col.accessor( 'contactPhone', {
			id: 'contactPhone',
			header: __( 'Phone', 'anacrm' ),
		} ),

		col.accessor( 'contactWebsite', {
			header: __( 'Web', 'anacrm' ),
			cell: ( { getValue } ) => {
				const v = getValue();
				return v ? (
					<a
						href={ v }
						target="_blank"
						rel="noopener noreferrer"
						className="text-blue hover:!no-underline truncate block max-w-[160px]"
					>
						{ v }
					</a>
				) : (
					'—'
				);
			},
		} ),

		col.accessor( ( row ) => taxNames( row.fields ), {
			id: 'fields',
			header: __( 'Category', 'anacrm' ),
			filterFn: taxFilterFn,
		} ),

		col.accessor( ( row ) => taxNames( row.sectors ), {
			id: 'sectors',
			header: __( 'Sector', 'anacrm' ),
			filterFn: taxFilterFn,
		} ),

		col.accessor( ( row ) => taxNames( row.networks ), {
			id: 'networks',
			header: __( 'Network', 'anacrm' ),
			filterFn: taxFilterFn,
		} ),

		col.accessor( ( row ) => taxNames( row.legalForms ), {
			id: 'legalForms',
			header: __( 'Legal form', 'anacrm' ),
			filterFn: taxFilterFn,
		} ),
	];
}
